/**
 * presets.ts — Bundled theme presets for clubmanager.
 *
 * Each preset maps CSS variable names (from tokens.ts) to override values.
 * Applying a preset replaces the Zustand overrides map entirely.
 * `night-ops` has an empty overrides map — applying it is equivalent to reset.
 *
 * Preset families (curated to 6 — see CLAUDE.md):
 *   Dark         — night-ops (default), carbon
 *   Light        — console, paper
 *   Alternative  — bondi, forest
 *
 * Every preset that is not night-ops overrides ALL color tokens so the result
 * is a full palette rebuild, not just an accent shift. The only exceptions are
 * tokens that intentionally inherit the base value (documented inline).
 */

export interface ThemePreset {
  id: 'night-ops' | 'carbon' | 'console' | 'paper' | 'bondi' | 'forest'
  label: string
  description: string
  /** Family bucket — drives section grouping in the preset gallery. */
  family: 'dark' | 'light' | 'alternative'
  /** Short marketing line shown as card subtitle. */
  overrides: Record<string, string>
  /** Swatch color shown in the preset card — usually the accent.
   * Bondi uses a gradient to convey the warm-cool split; everything else
   * uses a single CSS color so the round swatch reads as one tone. */
  swatch: string
}

export const THEME_PRESETS: ThemePreset[] = [
  // -----------------------------------------------------------------------
  // Dark family
  // -----------------------------------------------------------------------

  {
    id: 'night-ops',
    label: 'Night Ops',
    description: 'El tema original. Cian sobre azul marino profundo.',
    family: 'dark',
    overrides: {},
    swatch: '#4fc3f7',
  },

  {
    id: 'carbon',
    label: 'Carbon',
    description: 'Escala de grises pura. Estructura y tipografía sobre color.',
    family: 'dark',
    overrides: {
      // Surfaces — zero hue throughout
      '--bg-deep': 'oklch(12% 0 0)',
      '--bg': 'oklch(16% 0 0)',
      '--bg-2': 'oklch(20% 0 0)',
      '--panel': 'oklch(18% 0 0)',
      '--panel-2': 'oklch(22% 0 0)',
      '--line': 'oklch(30% 0 0)',
      '--line-strong': 'oklch(38% 0 0)',
      '--line-soft': 'rgba(255, 255, 255, 0.04)',
      // Text
      '--fg': 'oklch(92% 0 0)',
      '--fg-muted': 'oklch(70% 0 0)',
      '--fg-faint': 'oklch(50% 0 0)',
      // Accent — keep our brand cyan family (low chroma) for continuity
      '--accent': 'oklch(70% 0.04 240)',
      '--accent-soft': 'oklch(30% 0.02 240)',
      '--accent-hover': 'oklch(85% 0.03 240)',
      '--accent-fg': 'oklch(10% 0 0)',
      // Status — tiny hue hints only; intentionally near-neutral
      '--ok': 'oklch(70% 0.03 150)',
      '--warn': 'oklch(70% 0.04 85)',
      '--bad': 'oklch(65% 0.06 25)',
      '--info': 'oklch(70% 0.04 240)',
      // Scoped contexts — same grayscale ramp
      '--sidebar-bg': 'oklch(16% 0 0)',
      '--sidebar-fg': 'oklch(92% 0 0)',
      '--sidebar-fg-muted': 'oklch(70% 0 0)',
      '--sidebar-line': 'oklch(30% 0 0)',
      '--chrome-bg': 'oklch(16% 0 0)',
      '--chrome-fg': 'oklch(92% 0 0)',
      '--chrome-line': 'oklch(30% 0 0)',
      '--overlay-bg': 'oklch(18% 0 0)',
      '--overlay-panel-2': 'oklch(22% 0 0)',
      '--overlay-line': 'oklch(30% 0 0)',
    },
    swatch: 'oklch(70% 0.04 240)',
  },

  // -----------------------------------------------------------------------
  // Light family
  // -----------------------------------------------------------------------

  {
    id: 'console',
    label: 'Console',
    description: 'Tema claro. Superficies blancas, acento cian contrastado.',
    family: 'light',
    overrides: {
      // Surfaces — bright neutral
      '--bg-deep': 'oklch(96% 0.005 230)',
      '--bg': 'oklch(98% 0.003 230)',
      '--bg-2': 'oklch(95% 0.005 230)',
      '--panel': 'oklch(100% 0 0)',
      '--panel-2': 'oklch(97% 0.003 230)',
      '--line': 'oklch(88% 0.005 230)',
      '--line-strong': 'oklch(80% 0.008 230)',
      // Row highlights become dark-alpha on white backgrounds
      '--line-soft': 'rgba(0, 0, 0, 0.04)',
      // Text — dark, readable on white
      '--fg': 'oklch(20% 0.02 250)',
      '--fg-muted': 'oklch(42% 0.02 250)',
      '--fg-faint': 'oklch(62% 0.015 250)',
      // Accent — same brand-cyan hue family, shifted darker for light surfaces
      '--accent': 'oklch(45% 0.14 211)',
      '--accent-soft': 'oklch(94% 0.04 211)',
      '--accent-hover': 'oklch(35% 0.16 211)',
      '--accent-fg': '#fff',
      // Text on accent-soft: --accent-soft is light here, so on-accent-soft
      // must flip dark (the night-ops default is near-white).
      '--on-accent-soft': 'oklch(22% 0.04 211)',
      // Status — darkened to ensure contrast on white
      '--ok': 'oklch(45% 0.14 165)',
      '--warn': 'oklch(55% 0.16 75)',
      '--bad': 'oklch(48% 0.18 25)',
      '--info': 'oklch(45% 0.14 211)',
      // Scoped contexts — all light surfaces
      '--sidebar-bg': 'oklch(97% 0.005 230)',
      '--sidebar-fg': 'oklch(20% 0.02 250)',
      '--sidebar-fg-muted': 'oklch(42% 0.02 250)',
      '--sidebar-line': 'oklch(88% 0.005 230)',
      '--chrome-bg': 'oklch(100% 0 0)',
      '--chrome-fg': 'oklch(20% 0.02 250)',
      '--chrome-line': 'oklch(88% 0.005 230)',
      '--overlay-bg': 'oklch(100% 0 0)',
      '--overlay-panel-2': 'oklch(97% 0.003 230)',
      '--overlay-line': 'oklch(88% 0.005 230)',
    },
    swatch: 'oklch(45% 0.14 211)',
  },

  {
    id: 'paper',
    label: 'Paper',
    description: 'Crema cálida. Cómoda para lectura prolongada.',
    family: 'light',
    overrides: {
      // Surfaces — warm off-white, cream/sepia tones
      '--bg-deep': 'oklch(94% 0.018 80)',
      '--bg': 'oklch(97% 0.015 80)',
      '--bg-2': 'oklch(93% 0.02 80)',
      '--panel': 'oklch(99% 0.01 80)',
      '--panel-2': 'oklch(96% 0.015 80)',
      '--line': 'oklch(85% 0.02 70)',
      '--line-strong': 'oklch(78% 0.025 70)',
      // Row highlights — dark-alpha on warm backgrounds
      '--line-soft': 'rgba(0, 0, 0, 0.04)',
      // Text — warm dark; not pure black, reduces eye strain
      '--fg': 'oklch(28% 0.04 50)',
      '--fg-muted': 'oklch(48% 0.04 50)',
      '--fg-faint': 'oklch(65% 0.03 50)',
      // Accent — muted amber; low chroma for reading-comfort aesthetic
      '--accent': 'oklch(45% 0.13 65)',
      '--accent-soft': 'oklch(92% 0.05 65)',
      '--accent-hover': 'oklch(35% 0.15 65)',
      '--accent-fg': '#fff',
      // Text on accent-soft: cream-light here, so on-accent-soft must flip dark.
      '--on-accent-soft': 'oklch(28% 0.08 50)',
      // Status — muted; not jarring on cream
      '--ok': 'oklch(40% 0.12 150)',
      '--warn': 'oklch(50% 0.14 75)',
      '--bad': 'oklch(42% 0.16 25)',
      '--info': 'oklch(45% 0.13 65)',
      // Scoped contexts — warm light surfaces throughout
      '--sidebar-bg': 'oklch(96% 0.015 80)',
      '--sidebar-fg': 'oklch(28% 0.04 50)',
      '--sidebar-fg-muted': 'oklch(48% 0.04 50)',
      '--sidebar-line': 'oklch(85% 0.02 70)',
      '--chrome-bg': 'oklch(99% 0.01 80)',
      '--chrome-fg': 'oklch(28% 0.04 50)',
      '--chrome-line': 'oklch(85% 0.02 70)',
      '--overlay-bg': 'oklch(99% 0.01 80)',
      '--overlay-panel-2': 'oklch(96% 0.015 80)',
      '--overlay-line': 'oklch(85% 0.02 70)',
    },
    swatch: 'oklch(45% 0.13 65)',
  },

  // -----------------------------------------------------------------------
  // Alternative family — bolder personality, opt-in only
  // -----------------------------------------------------------------------

  {
    id: 'bondi',
    label: 'Bondi',
    description: 'Mar y sol. Cian en azul profundo con un toque dorado de alerta.',
    family: 'alternative',
    overrides: {
      // Surfaces — Bondi navy #023047 base
      '--bg-deep': 'oklch(20% 0.05 235)',
      '--bg': 'oklch(28% 0.06 234)',
      '--bg-2': 'oklch(33% 0.06 232)',
      '--panel': 'oklch(28% 0.06 234)',
      '--panel-2': 'oklch(36% 0.06 230)',
      '--line': 'oklch(43% 0.06 228)',
      '--line-strong': 'oklch(52% 0.07 225)',
      '--line-soft': 'rgba(255, 255, 255, 0.05)',
      // Text — sky-tinted whites
      '--fg': 'oklch(95% 0.02 220)',
      '--fg-muted': 'oklch(78% 0.04 220)',
      '--fg-faint': 'oklch(60% 0.05 220)',
      // Accent — Bondi #219EBC cyan
      '--accent': 'oklch(62% 0.10 215)',
      '--accent-soft': 'oklch(35% 0.07 215)',
      '--accent-hover': 'oklch(78% 0.10 215)',
      '--accent-fg': 'oklch(15% 0.03 235)',
      // Status — gold warn (#FFB703) is the Bondi signature.
      // Bad is shifted toward Bondi orange (#FB8500) so warm hues stay coherent.
      '--ok': 'oklch(72% 0.14 162)',
      '--warn': 'oklch(82% 0.16 75)',
      '--bad': 'oklch(70% 0.18 35)',
      '--info': 'oklch(62% 0.10 215)',
      // Scoped contexts — same Bondi-navy base
      '--sidebar-bg': 'oklch(28% 0.06 234)',
      '--sidebar-fg': 'oklch(95% 0.02 220)',
      '--sidebar-fg-muted': 'oklch(78% 0.04 220)',
      '--sidebar-line': 'oklch(43% 0.06 228)',
      '--chrome-bg': 'oklch(28% 0.06 234)',
      '--chrome-fg': 'oklch(95% 0.02 220)',
      '--chrome-line': 'oklch(43% 0.06 228)',
      '--overlay-bg': 'oklch(28% 0.06 234)',
      '--overlay-panel-2': 'oklch(36% 0.06 230)',
      '--overlay-line': 'oklch(43% 0.06 228)',
    },
    // Gradient swatch — communicates the warm-cool split at a glance.
    swatch: 'linear-gradient(135deg, oklch(62% 0.10 215) 0%, oklch(82% 0.16 75) 100%)',
  },

  {
    id: 'forest',
    label: 'Forest',
    description: 'Verde apagado. Cálido, natural, descansado.',
    family: 'alternative',
    overrides: {
      '--accent': 'oklch(62% 0.11 155)',
      '--accent-soft': 'oklch(21% 0.05 155)',
      '--accent-hover': 'oklch(80% 0.09 155)',
      '--accent-fg': 'oklch(9% 0.02 155)',
      '--bg-deep': 'oklch(7% 0.015 100)',
      '--bg': 'oklch(11% 0.02 100)',
      '--bg-2': 'oklch(14% 0.025 100)',
      '--panel': 'oklch(11% 0.02 100)',
      '--panel-2': 'oklch(15% 0.025 100)',
      '--sidebar-bg': 'oklch(11% 0.02 100)',
      '--chrome-bg': 'oklch(11% 0.02 100)',
      '--overlay-bg': 'oklch(11% 0.02 100)',
      '--info': 'oklch(62% 0.11 155)',
      '--ok': 'oklch(68% 0.13 148)',
    },
    swatch: 'oklch(62% 0.11 155)',
  },
]
